import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

export const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="section-padding bg-background-secondary relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="container-luxury relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-accent font-body text-sm tracking-[0.3em] uppercase mb-4 block">
            Qui suis-je
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight">
            <span className="text-gradient-gold">GILLES DELIEUZE</span>
          </h2>
          <p className="text-lg text-muted-foreground font-body mt-4">
            Mentor • Auteur • Enseignant
          </p>
        </motion.div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-accent/20 blur-3xl rounded-full" />
            <div className="relative">
              <img
                src="https://gillesdelieuze.com/wp-content/uploads/2022/10/Gilles-2a-650x650.jpg"
                alt="Gilles Delieuze"
                className="rounded-3xl w-full max-w-md mx-auto border border-accent/20 shadow-gold"
              />
              {/* Stats overlay */}
              <motion.div
                className="absolute -bottom-6 -right-6 glass-gold rounded-2xl p-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.6 }}
              >
                <p className="text-3xl font-display text-gradient-gold">25 ans</p>
                <p className="text-sm text-muted-foreground font-body">d'expérience</p>
              </motion.div>
            </div>
          </motion.div>

          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="space-y-6"
          >
            <p className="text-foreground font-body text-lg leading-relaxed">
              Je m'appelle Gilles Delieuze, je suis <span className="text-accent">praticien en soins énergétiques</span> et 
              <span className="text-accent"> guide spirituel</span>.
            </p>
            
            <p className="text-muted-foreground font-body leading-relaxed">
              Cela fait 25 ans que j'accompagne des personnes en quête d'un mieux-être et 17 ans que je suis un expert Dauphins.
            </p>

            <p className="text-muted-foreground font-body leading-relaxed">
              Enfant j'aimais dire que "plus tard je serai magicien pour aider les gens à être heureux". 
              Et c'est ce que j'ai la chance d'accomplir aujourd'hui.
            </p>

            <div className="glass-card rounded-2xl p-6 my-8">
              <p className="text-foreground font-body italic leading-relaxed">
                "Je sais la souffrance que cela génère de ne pas se sentir à sa place. D'être bloqué dans 
                des schémas répétitifs. Ou de se sentir déconnecté du monde."
              </p>
            </div>

            <p className="text-muted-foreground font-body leading-relaxed">
              J'ai moi même fait face à une période de grands questionnements. Jusqu'au jour où…
            </p>

            <div className="luxury-card-gold rounded-2xl p-6">
              <p className="text-foreground font-body leading-relaxed mb-4">
                <span className="text-accent font-semibold">Je reçois un "appel" mystique.</span> Nous sommes en 2007. 
                Il est 3h33. Et une énergie se présente à moi sous le nom de "Maîtres Dauphins".
              </p>
              <p className="text-muted-foreground font-body text-sm">
                Cet appel m'a permis de sortir la tête de l'eau, de mon ego et de toutes ces mémoires 
                qui m'empêchaient d'être heureux dans ma vie.
              </p>
            </div>
          </motion.div>
        </div>

        {/* YouTube Stats */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="mt-20"
        >
          <div className="glass-card rounded-3xl p-8 md:p-12 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-foreground font-body leading-relaxed mb-4">
                  De nombreux médias spécialisés dans la spiritualité m'ont invité sur leurs plateaux 
                  pour raconter mon histoire.
                </p>
                <p className="text-accent font-body text-lg">
                  Sur YouTube, une de mes interviews atteint même le demi-million de spectateurs !
                </p>
              </div>
              <div className="relative">
                <img
                  src="https://gillesdelieuze.com/wp-content/uploads/2024/11/Capture-decran-2024-11-07-a-15.15.33-1024x792.png"
                  alt="YouTube stats"
                  className="rounded-2xl border border-accent/20"
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
